/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Box } from '@mantine/core';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { colorToken } from '@fai/design-tokens';
import { envIs } from '@fai/friendli-suite/shared/environments/conditionals';
import type { PropsWithChildren } from '@fai/util/types';

import { hideWrapper, hideLabel } from './hide-from-release.css';

interface HideFromReleaseState {
  isWrapperVisible: boolean;
  toggleIsWrapperVisible: VoidFunction;
}

export const useHideFromReleaseStore = create<HideFromReleaseState>()(
  persist(
    (set) => ({
      isWrapperVisible: false,
      toggleIsWrapperVisible: () => set((state) => ({ isWrapperVisible: !state.isWrapperVisible })),
    }),
    {
      name: 'hide-from-release-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const hideOptionFromRelease = <T,>(target: T) =>
  envIs.PRODUCTION || envIs.STAGING ? [] : [target];

export function HideFromRelease({
  children,
  isReleaseTarget = true,
}: PropsWithChildren<{ isReleaseTarget?: boolean }>) {
  const { isWrapperVisible } = useHideFromReleaseStore();

  if (envIs.PRODUCTION || envIs.STAGING) return null;

  if (envIs.DEV && !isReleaseTarget) return null;

  if (!isWrapperVisible) return children;

  const color = isReleaseTarget
    ? colorToken.core.color.lime[700]
    : colorToken.color.fg.warning.default.base;

  return (
    <Box className={hideWrapper} component="span" style={{ borderColor: color }}>
      <Box className={hideLabel} component="span" style={{ backgroundColor: color }}>
        {isReleaseTarget ? 'QA' : 'dev'}
      </Box>

      {children}
    </Box>
  );
}
